import { Fragment, useCallback, useState } from 'react';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { pluralizeWord } from '@shared/helpers/formatters';
import { type AnnotatedApiQueryMethod } from '@shared/utils/makeApiQuery';

import { type GetApplicationApiResponsePayload } from '@api/applications/general';

import { Button } from '@ui/uikit/components/common/Button';
import { type FeedbackButtonGroupSelection } from '@ui/uikit/components/common/FeedbackButtonGroup';

import { AddReviewDialog, AddSuggestionDialog } from '../../AddFeedbackDialog';
import { type FeedbackItem, type FeedbackType } from '../../ApplicationUserFeedbackCarousel';
import { ViewAllFeedbackItemsDialog } from '../../ViewAllFeedbackItemsDialog';
import styles from './FeedbackSectionButtons.module.scss';

type FeedbackSectionButtonsProps = {
  application: GetApplicationApiResponsePayload;
  getItems: AnnotatedApiQueryMethod<[applicationSlug: string], FeedbackItem[]>;
  feedbackType: FeedbackType;
  onHelpfulnessToggle: (selection: FeedbackButtonGroupSelection, id: number) => void;
  onFunnyReactionToggle: (state: 'on' | 'off', id: number) => void;
  viewAllDisabled?: boolean;
};

const FeedbackSectionButtons = ({
  application,
  feedbackType,
  viewAllDisabled,
  ...props
}: FeedbackSectionButtonsProps) => {
  const { id: applicationId, app_id: applicationSlug } = application;

  const [addDialogIsOpen, setAddDialogIsOpen] = useState(false);
  const [viewDialogIsOpen, setViewDialogIsOpen] = useState(false);

  const handleAddFeedbackDialogDismissed = useCallback(() => {
    setAddDialogIsOpen(false);
  }, []);

  const handleViewAllFeedbackItemsDialogClose = useCallback(() => {
    setViewDialogIsOpen(false);
  }, []);

  const { user } = useAuth();
  const { open: openAuthModal } = useAuthModal();

  const isSuggestion = feedbackType === 'Suggestion';
  const isReview = feedbackType === 'Review';

  return (
    <Fragment>
      <div className={styles['buttons-container']}>
        <Button
          variant="primary"
          onClick={() => (user ? setAddDialogIsOpen(true) : openAuthModal())}
        >
          {`Add ${feedbackType}`}
        </Button>
        <Button
          variant="secondary"
          onClick={() => setViewDialogIsOpen(true)}
          disabled={viewAllDisabled}
        >
          {`View ${pluralizeWord(feedbackType)}`}
        </Button>
      </div>

      {isReview && (
        <AddReviewDialog
          applicationSlug={applicationSlug}
          applicationId={applicationId}
          isOpen={addDialogIsOpen}
          onOpenChange={handleAddFeedbackDialogDismissed}
        />
      )}

      <ViewAllFeedbackItemsDialog
        application={application}
        isOpen={viewDialogIsOpen}
        onClose={handleViewAllFeedbackItemsDialogClose}
        onAddFeedbackItem={() => (user ? setAddDialogIsOpen(true) : openAuthModal())}
        feedbackType={feedbackType}
        {...props}
      />

      {isSuggestion && (
        <AddSuggestionDialog
          applicationSlug={applicationSlug}
          applicationId={applicationId}
          isOpen={addDialogIsOpen}
          onOpenChange={handleAddFeedbackDialogDismissed}
        />
      )}
    </Fragment>
  );
};

export { FeedbackSectionButtons };
